import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css, createGlobalStyle } from 'styled-components';
import en from '../../language/Eng.json';

const FontStyle = createGlobalStyle`
  @font-face {
    font-family: 'AmaticSC-Bold';
    src: url('/font/AmaticSC-Bold.ttf') format('truetype');
    font-weight: bold;
    font-style: normal;
    font-display: swap;
  }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-20px); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const Nav = styled.nav<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  padding: 35px 40px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 35px 20px 20px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 180%;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.75) 0%, rgba(255, 255, 255, 0.2) 60%, rgba(255, 255, 255, 0) 100%);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    mask-image: linear-gradient(to bottom, black 40%, transparent 100%);
    -webkit-mask-image: linear-gradient(to bottom, black 40%, transparent 100%);
    z-index: -1;
    pointer-events: none;
    opacity: ${props => (props.isOpen ? 0 : 1)};
    transition: opacity 0.3s ease;

    @media (max-width: 768px) {
      height: 140%;
    }
  }
`;

const LogoWrapper = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  height: clamp(50px, 8vw, 80px);
  cursor: pointer;

  img {
    height: 100%;
    width: auto;
    object-fit: contain;
    opacity: 0;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-color: #9e5e04;
    mask-image: url("image/logo/Joie_Logo_oulined2.png");
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-image: url("image/logo/Joie_Logo_oulined2.png");
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(10px, 3vw, 20px);
  margin-left: auto;
`;

const MenuIconButton = styled.div<{ isOpen: boolean }>`
  width: 25px;
  height: 18px;
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: pointer;
  z-index: 1002;
  padding: 5px;
  box-sizing: content-box;

  span {
    display: block;
    width: 25px;
    height: 2px;
    background-color: #9e5e04;
    transition: all 0.3s ease-in-out;
    position: absolute;
    left: 5px;
  }

  span:nth-child(1) { top: 5px; }
  span:nth-child(2) { top: 13px; }
  span:nth-child(3) { top: 21px; }

  ${props => props.isOpen && css`
    span:nth-child(1) {
      top: 13px;
      transform: rotate(45deg);
    }
    span:nth-child(2) {
      opacity: 0;
      transform: translateX(-10px);
    }
    span:nth-child(3) {
      top: 13px;
      transform: rotate(-45deg);
    }
  `}
`;

const MenuOverlay = styled.div<{ isRendered: boolean; isOpen: boolean; closeDelay: number }>`
  position: fixed;
  inset: 0;
  background-color: #F4F1E6;
  display: ${props => (props.isRendered ? 'flex' : 'none')};
  z-index: 999;
  padding: 100px 40px 40px 40px;
  color: #9e5e04;
  flex-direction: column;
  overflow-y: auto;
  box-sizing: border-box;

  ${props => props.isOpen ? css`
    animation: ${fadeIn} 0.3s ease forwards;
  ` : css`
    animation: ${fadeOut} 0.5s ease forwards;
    animation-delay: ${props.closeDelay}s;
  `}
`;

const MenuItem = styled.h1<{ index: number; isOpen: boolean; total: number }>`
  font-family: 'AmaticSC-Bold', cursive;
  font-size: clamp(2rem, 8vw, 3.5rem);
  margin: 0;
  cursor: pointer;
  opacity: ${props => (props.isOpen ? 0 : 1)};
  transition: opacity 0.2s ease;

  ${props => props.isOpen ? css`
    animation: ${slideDown} 0.5s ease forwards;
    animation-delay: ${0.3 + props.index * 0.1}s;
  ` : css`
    animation: ${slideUp} 0.4s ease forwards;
    animation-delay: ${(props.total - props.index - 1) * 0.08}s;
  `}

  &:hover {
    opacity: 0.4 !important;
  }
`;

const SideImage = styled.img<{ isOpen: boolean }>`
  width: 100%;
  max-width: 350px;
  padding-right: 5%;
  height: auto;
  object-fit: cover;
  opacity: ${props => (props.isOpen ? 0 : 1)};

  ${props => props.isOpen ? css`
    animation: ${slideDown} 0.5s ease forwards;
    animation-delay: 0.9s;
  ` : css`
    animation: ${slideUp} 0.4s ease forwards;
    animation-delay: 0s;
  `}
`;

const FooterContainer = styled.div<{ isOpen: boolean }>`
  margin-top: auto;
  padding-top: 40px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
  opacity: ${props => (props.isOpen ? 0 : 1)};

  ${props => props.isOpen ? css`
    animation: ${slideDown} 0.5s ease forwards;
    animation-delay: 1.0s;
  ` : css`
    animation: ${slideUp} 0.4s ease forwards;
    animation-delay: 0s;
  `}
`;

const FooterLogo = styled.div`
  height: clamp(60px, 15vw, 120px);
  position: relative;
  display: inline-block;
  
  img {
    height: 100%;
    width: auto;
    object-fit: contain;
    opacity: 0;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-color: #9e5e04;
    mask-image: url("image/logo/Joie_Logo_oulined.png");
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
    -webkit-mask-image: url("image/logo/Joie_Logo_oulined.png");
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
  }
`;

const TopMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const navigate = useNavigate();
  const menuItems = en?.fullMenu?.items || [];
  const overlayCloseDelay = menuItems.length * 0.08 + 0.2;

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
    } else {
      const delay = (overlayCloseDelay + 0.5) * 1000;
      const timer = setTimeout(() => setIsRendered(false), delay);
      return () => clearTimeout(timer);
    }
  }, [isOpen, overlayCloseDelay]);

  const handleMenuClick = (path: string) => {
    setIsOpen(false);
    if (path.startsWith('http')) {
      setTimeout(() => {
        window.open(path, '_blank', 'noopener,noreferrer');
      }, (overlayCloseDelay + 0.5) * 1000);
    } else {
      setTimeout(() => navigate(path), (overlayCloseDelay + 0.5) * 1000);
    }
  };

  return (
    <>
      <FontStyle />
      <Nav isOpen={isOpen}>
        <LogoWrapper onClick={() => handleMenuClick('/')}>
          <img src="image/logo/Joie_Logo_oulined2.png" alt="Joie Logo" />
        </LogoWrapper>
        <RightSection>
          <MenuIconButton isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
            <span /><span /><span />
          </MenuIconButton>
        </RightSection>
      </Nav>

      <MenuOverlay isRendered={isRendered} isOpen={isOpen} closeDelay={overlayCloseDelay}>
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {menuItems.map((item, index) => (
              <MenuItem
                key={item.label}
                index={index}
                isOpen={isOpen}
                total={menuItems.length}
                onClick={() => handleMenuClick(item.path)}
              >
                {item.label}
              </MenuItem>
            ))}
          </div>
          <SideImage src="/image/background/back6.webp" alt="Menu Side" isOpen={isOpen} />
        </div>

        <FooterContainer isOpen={isOpen}>
          <div style={{ fontSize: '0.8rem' }}>
            <p style={{ whiteSpace: 'pre-line' }}>{en.fullMenu.hours}</p>
            <p
              style={{ whiteSpace: 'pre-line', marginTop: '10px', cursor: 'pointer' }}
              onClick={(e) => {
                e.preventDefault();
                window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(en.fullMenu.address.replace(/\n/g, ' '))}`, '_blank');
              }}
            >
              {en.fullMenu.address}
            </p>
          </div>
          <FooterLogo>
            <img src="image/logo/Joie_Logo_oulined.png" alt="Footer Logo" />
          </FooterLogo>
        </FooterContainer>
      </MenuOverlay>
    </>
  );
};

export default TopMenu;